import {
  BellIcon,
  HomeIcon,
  LogOutIcon,
  MessageCircleHeartIcon,
  MoreVerticalIcon,
  PlusIcon,
  SettingsIcon,
  UserIcon,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useEffect, useRef, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { GroupResponseDto } from '@splitfiesta/shared';

import { GroupSelectorModal } from '@/components/home/GroupSelectorModal';

import { useAllGroupsWithBalancesQuery } from '@/hooks/queries/useGroups';
import { useUnseenNotificationCount } from '@/hooks/queries/useNotifications';
import { useRouter } from '@/hooks/useRouteChangeRouter';

import { queryClient } from '@/lib/queryClient';
import { cn } from '@/lib/utils';

import { useAuthStore } from '@/stores/authStore';

export function GlobalMobileNav() {
  const location = useLocation();
  const pathname = location.pathname;
  const { t } = useTranslation();
  const router = useRouter();
  const user = useAuthStore(state => state.user);
  const logout = useAuthStore(state => state.logout);
  const unseenCount = useUnseenNotificationCount();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load auth namespace for logout text
  useNamespace('auth');
  // Load expenses namespace for add button
  useNamespace('expenses');

  // Extract group ID from the current path if we're on a group page
  // Exclude 'create' as it's a route, not a group ID
  const groupIdMatch = pathname.match(/^\/groups\/([a-zA-Z0-9-]+)/)?.[1];
  const currentGroupId =
    groupIdMatch && groupIdMatch !== 'create' ? groupIdMatch : undefined;

  // Fetch groups for the Add Expense button
  // React Query handles caching - no duplicate API call if data is already fetched
  const { data: groups = [], isLoading: isLoadingGroups } =
    useAllGroupsWithBalancesQuery();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMenuOpen]);

  const handleLogout = () => {
    queryClient.clear();
    logout();
    router.push('/login');
  };

  const handleAddExpense = () => {
    // If we're on a group page, create expense in that group
    if (currentGroupId) {
      router.push(`/groups/${currentGroupId}/expenses/create`);
      return;
    }

    // Handle based on number of groups
    if (groups.length === 0) {
      // No groups, redirect to create group
      router.push('/groups/create');
    } else if (groups.length === 1) {
      // Single group, go directly to expense creation
      router.push(`/groups/${groups[0].id}/expenses/create`);
    } else {
      // Multiple groups - show selector modal
      setIsGroupModalOpen(true);
    }
  };

  const handleGroupSelect = (group: GroupResponseDto) => {
    setIsGroupModalOpen(false);
    router.push(`/groups/${group.id}/expenses/create`);
  };

  if (!user) {
    return null;
  }

  const tabs = [
    {
      name: t('common:navigation.groups'),
      href: '/groups',
      icon: HomeIcon,
      testId: 'mobile-nav-groups',
    },
    {
      name: t('common:navigation.activity'),
      href: '/activity',
      icon: MessageCircleHeartIcon,
      testId: 'mobile-nav-activity',
    },
    {
      name: t('expenses:add'),
      href: '/expenses/create',
      icon: PlusIcon,
      isPrimary: true,
      testId: 'mobile-nav-add',
    },
    {
      name: t('common:navigation.notifications'),
      href: '/notifications',
      icon: BellIcon,
      testId: 'mobile-nav-notifications',
      badge: unseenCount > 0 ? unseenCount : undefined,
    },
    {
      name: t('common:more'),
      href: '#',
      icon: MoreVerticalIcon,
      testId: 'mobile-nav-more',
      isMenu: true,
    },
  ];

  const isActive = (href: string) => {
    if (href === '/activity' && pathname === '/activity') {
      return true;
    }
    if (href === '/profile' && pathname === '/profile') {
      return true;
    }
    if (href === '/notifications' && pathname === '/notifications') {
      return true;
    }
    if (href === '/groups' && pathname.startsWith('/groups')) {
      return true;
    }
    return false;
  };

  return (
    <div
      className="fixed left-4 right-4 sm:hidden z-50"
      style={{
        // Use 100dvh-based positioning for iOS Safari compatibility
        // Position above the safe area for floating effect
        bottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))',
      }}
      data-testid="global-mobile-navigation"
    >
      {/* Liquid Glass floating nav container */}
      <div className="relative bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl rounded-3xl border border-white/20 dark:border-gray-700/50 shadow-lg shadow-black/10 dark:shadow-black/30">
        <nav
          className="flex justify-around items-center h-16 px-2"
          data-testid="global-mobile-nav-container"
        >
          {tabs.map(tab => {
            const active = isActive(tab.href) && !tab.isPrimary;
            const Icon = tab.icon;

            if (tab.isPrimary) {
              return (
                <button
                  key={tab.name}
                  onClick={handleAddExpense}
                  disabled={isLoadingGroups}
                  aria-label={t('expenses:actions.addExpense')}
                  className="flex flex-col items-center justify-center flex-1"
                  data-testid={tab.testId}
                >
                  <div className="bg-linear-to-r from-orange-500 to-red-500 dark:from-orange-400 dark:to-red-400 text-white rounded-full p-3 shadow-lg transform hover:scale-105 transition-transform">
                    <Icon className="h-6 w-6" />
                  </div>
                </button>
              );
            }

            // Handle More menu specially
            if (tab.isMenu) {
              return (
                <div key={tab.name} className="flex-1 relative" ref={menuRef}>
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="w-full flex flex-col items-center justify-center py-2 text-gray-500 dark:text-gray-400"
                    data-testid={tab.testId}
                  >
                    <Icon className="h-5 w-5 text-gray-400" />
                    <span className="text-[10px] mt-0.5">{tab.name}</span>
                  </button>

                  {/* Dropdown Menu */}
                  {isMenuOpen && (
                    <div className="absolute bottom-full right-0 mb-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2">
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsMenuOpen(false)}
                        data-testid="mobile-menu-profile"
                      >
                        <UserIcon className="w-4 h-4 mr-3" />
                        {t('common:navigation.profile')}
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsMenuOpen(false)}
                        data-testid="mobile-menu-settings"
                      >
                        <SettingsIcon className="w-4 h-4 mr-3" />
                        {t('common:navigation.settings')}
                      </Link>
                      <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        data-testid="mobile-menu-logout"
                      >
                        <LogOutIcon className="w-4 h-4 mr-3" />
                        {t('auth:actions.logout')}
                      </button>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={tab.name}
                to={tab.href}
                className={cn(
                  'flex flex-col items-center justify-center flex-1 py-2 relative',
                  active
                    ? 'text-orange-500 dark:text-orange-400'
                    : 'text-gray-500 dark:text-gray-400'
                )}
                data-testid={tab.testId}
              >
                <div className="relative">
                  <Icon
                    className={cn(
                      'h-5 w-5',
                      active
                        ? 'text-orange-500 dark:text-orange-400'
                        : 'text-gray-400'
                    )}
                  />
                  {tab.badge && (
                    <span className="absolute -top-1.5 -right-2.5 bg-red-500 text-white text-[10px] rounded-full min-w-[14px] h-3.5 px-0.5 flex items-center justify-center">
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </div>
                <span className="text-[10px] mt-0.5">{tab.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* Group Selector Modal for Add Expense */}
        {isGroupModalOpen && groups.length > 1 && (
          <GroupSelectorModal
            groups={groups}
            onSelectGroup={handleGroupSelect}
            onClose={() => setIsGroupModalOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
